import { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, Trash, Camera, FileImage, Signature } from "@phosphor-icons/react";

const LABELS = [
  { v: "canhoto", l: "Canhoto/CTe (baixa MDFe)" },
  { v: "carregamento", l: "Carregamento" },
  { v: "descarga", l: "Descarga" },
  { v: "vale_pedagio", l: "Vale-Pedágio / Recibo" },
  { v: "nota_peca", l: "Nota de peça/serviço" },
  { v: "ocorrencia", l: "Ocorrência" },
  { v: "outro", l: "Outro" },
];

export default function DocumentsDialog({ trip, initialLabel = "canhoto", autoOpenCamera = false, onClose }) {
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState(initialLabel);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const token = localStorage.getItem("token");

  const load = async () => {
    if (!trip) return;
    const { data } = await api.get("/documents", { params: { trip_id: trip.id } });
    setDocs(data);
  };
  useEffect(() => {
    if (trip) {
      setLabel(initialLabel);
      load();
      if (autoOpenCamera) {
        // Give the dialog a beat to mount, then trigger the camera on mobile
        setTimeout(() => { try { camRef.current?.click(); } catch {} }, 400);
      }
    }
    // eslint-disable-next-line
  }, [trip?.id, initialLabel, autoOpenCamera]);

  const upload = async (file, docLabel) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/documents", { trip_id: trip.id, label: docLabel, file_id: up.data.id });
      toast.success(docLabel === "canhoto" ? "Canhoto/CTe anexado ao frete ✅" : "Documento enviado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no upload"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir documento?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  if (!trip) return null;

  const canhotos = docs.filter((d) => d.label === "canhoto");

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Signature size={22} weight="duotone" className="text-blue-600" />
            Documentos do Frete — {trip.origin} → {trip.destination}
          </DialogTitle>
        </DialogHeader>

        {/* Quick capture — canhoto shortcut */}
        <div className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm font-bold text-blue-900 flex items-center gap-1.5">
                <Signature size={18} weight="fill" /> Canhoto / CTe assinado
              </div>
              <p className="text-xs text-slate-700 mt-1">
                Bata a foto do canhoto assinado direto pela câmera. Ele fica anexado a este frete para baixa do MDFe.
              </p>
              {canhotos.length > 0 && (
                <p className="text-xs text-emerald-700 font-semibold mt-1">
                  ✅ {canhotos.length} canhoto(s) já anexado(s)
                </p>
              )}
            </div>
            <div>
              <input
                ref={camRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => upload(e.target.files?.[0], "canhoto")}
                data-testid="doc-camera-input"
              />
              <Button
                data-testid="btn-shoot-canhoto"
                onClick={() => camRef.current?.click()}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-2 h-11"
              >
                <Camera size={20} weight="fill" /> Bater foto do canhoto
              </Button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 border-b pb-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Tipo do documento</Label>
            <Select value={label} onValueChange={setLabel}>
              <SelectTrigger data-testid="doc-label-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LABELS.map(x => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 items-end">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => upload(e.target.files?.[0], label)} data-testid="doc-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload-file" variant="outline" className="gap-1.5"><UploadSimple size={16} /> Arquivo/PDF</Button>
          </div>
        </div>

        {uploading && <p className="text-sm text-muted-foreground">Enviando…</p>}

        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Nenhum documento anexado ainda.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {docs.map((d) => (
              <div key={d.id} className={`border rounded-md overflow-hidden group relative ${d.label === "canhoto" ? "border-blue-400 ring-2 ring-blue-100" : "bg-slate-50"}`}>
                <a href={`${API}/files/${d.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="block">
                  <div className="aspect-square bg-white flex items-center justify-center">
                    <img src={`${API}/files/${d.file_id}?auth=${token}`} alt={d.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }} />
                    <div className="hidden items-center justify-center text-slate-400"><FileImage size={40} /></div>
                  </div>
                </a>
                <div className="p-2 flex items-center justify-between gap-1">
                  <span className={`text-xs font-semibold uppercase truncate ${d.label === "canhoto" ? "text-blue-800" : "text-blue-700"}`}>{LABELS.find(x => x.v === d.label)?.l || d.label}</span>
                  <Button size="icon" variant="ghost" onClick={() => remove(d.id)} data-testid={`btn-del-doc-${d.id}`}><Trash size={14} className="text-red-600" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
